import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { motion } from "framer-motion";
import {
  Minus, Plus, ShoppingBag, Check, Truck, ShieldCheck, RotateCcw, Loader2, ChevronLeft, PackageSearch,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { getProduct, formatPrice, proxyImage } from "@/lib/api";
import { BackorderDialog } from "@/components/BackorderDialog";
import { SITE_URL } from "@/lib/siteConfig";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function ProductDetail() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { t, tx, lang } = useI18n();

  const [base, setBase] = useState(null);
  const [localized, setLocalized] = useState(null); // {title, description, description_html, seo}
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState({}); // optionName -> value
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [backorderOpen, setBackorderOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setQty(1);
    setActiveImage(0);
    setLocalized(null);
    window.scrollTo({ top: 0 });

    // 1) Render instantly in English — never block the page on (slow) translation.
    getProduct(handle, "en")
      .then((p) => {
        if (cancelled) return;
        setBase(p);
        // default selection = first available variant's options
        const def = (p.variants || []).find((v) => v.available) || p.variants?.[0];
        if (def?.options) {
          const init = {};
          def.options.forEach((o) => { init[o.name] = o.value; });
          setSelected(init);
        }
      })
      .catch((e) => { if (!cancelled && e?.response?.status === 404) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // 2) Swap in localised text when ready (first view of a product may take a few seconds).
    if (lang && lang !== "en") {
      getProduct(handle, lang)
        .then((p) => {
          if (cancelled) return;
          setLocalized({
            title: p.title,
            description: p.description,
            description_html: p.description_html,
            seo: p.seo,
          });
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
  }, [handle, lang]);

  // Displayed product = English base with localised text merged in once it arrives.
  const product = useMemo(() => {
    if (!base) return null;
    if (lang !== "en" && localized) return { ...base, ...localized };
    return base;
  }, [base, localized, lang]);

  // Build option name -> [values] map
  const optionGroups = useMemo(() => {
    if (!product?.variants?.length) return [];
    const map = new Map();
    for (const v of product.variants) {
      for (const o of v.options || []) {
        if (!map.has(o.name)) map.set(o.name, new Set());
        map.get(o.name).add(o.value);
      }
    }
    return Array.from(map.entries()).map(([name, set]) => ({ name, values: Array.from(set) }));
  }, [product]);

  // Resolve the variant matching the current selection
  const currentVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    const match = product.variants.find((v) =>
      (v.options || []).every((o) => selected[o.name] === o.value)
    );
    return match || product.variants.find((v) => v.available) || product.variants[0];
  }, [product, selected]);

  const isValueAvailable = useCallback(
    (optName, value) => {
      if (!product?.variants) return false;
      const trial = { ...selected, [optName]: value };
      return product.variants.some(
        (v) => v.available && (v.options || []).every((o) => trial[o.name] === o.value)
      );
    },
    [product, selected]
  );

  const images = useMemo(() => {
    if (!product) return [];
    const list = (product.images || []).map((i) => i.url).filter(Boolean);
    if (!list.length && product.featured_image) list.push(product.featured_image);
    return list;
  }, [product]);

  const maxStock = (currentVariant && Number.isFinite(currentVariant.quantity_available) && currentVariant.quantity_available > 0)
    ? currentVariant.quantity_available
    : 99;
  const lowStock = currentVariant && Number.isFinite(currentVariant.quantity_available)
    && currentVariant.quantity_available > 0 && currentVariant.quantity_available <= 5;

  // keep selected quantity within the available stock when the variant changes
  useEffect(() => {
    setQty((q) => Math.max(1, Math.min(q, maxStock)));
  }, [currentVariant?.id, maxStock]);

  const handleAdd = () => {
    if (!currentVariant || !currentVariant.available) return;
    const res = addItem(
      {
        variantId: currentVariant.id,
        handle: product.handle,
        title: product.title,
        variantTitle: currentVariant.title,
        image: proxyImage(product.featured_image || images[0], 200),
        price: currentVariant.price,
        options: currentVariant.options,
        brand: product.vendor,
        available: currentVariant.available,
        maxQty: maxStock,
      },
      qty
    );
    if (res?.clamped) {
      toast.info(`${tx("Only")} ${res.cap} ${tx("in stock")}`, {
        description: tx("We've set the quantity to the maximum available."),
      });
    }
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  if (notFound) {
    return (
      <div className="container-site py-24 text-center">
        <h1 className="font-display text-3xl">Product not found</h1>
        <p className="mt-3 text-muted-foreground">This item may no longer be available.</p>
        <Button asChild className="mt-6"><LangLink to="/brand/kentucky">Back to shop</LangLink></Button>
      </div>
    );
  }

  const price = currentVariant?.price || product?.price_min;
  const jsonLd = product ? {
    "@context": "https://schema.org", "@type": "Product",
    name: product.title,
    image: images.map((u) => proxyImage(u, 1200)),
    description: product.seo?.description || product.description,
    brand: { "@type": "Brand", name: product.vendor || "Kentucky Horsewear" },
    sku: (product.variants || []).find((v) => v.sku)?.sku || product.handle,
    mpn: (product.variants || []).find((v) => v.sku)?.sku || product.handle,
    offers: {
      "@type": "Offer",
      priceCurrency: price?.currency || "EUR",
      price: price?.amount,
      priceValidUntil: new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10),
      itemCondition: "https://schema.org/NewCondition",
      availability: currentVariant?.available
        ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: `${SITE_URL}/product/${product.handle}`,
      seller: { "@type": "Organization", name: "Boomerang.ie" },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: ["IE", "GB", "EU"],
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 14,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: "0", currency: price?.currency || "EUR" },
        shippingDestination: ["IE", "GB", "DE", "BE", "NL", "FI", "US", "CA"].map((c) => ({
          "@type": "DefinedRegion", addressCountry: c,
        })),
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 5, unitCode: "DAY" },
        },
      },
    },
  } : null;

  return (
    <>
      {product && (
        <Seo
          title={product.seo?.title || product.title}
          description={product.seo?.description || (product.description || "").slice(0, 160)}
          path={`/product/${handle}`}
          image={proxyImage(product.featured_image, 1200)}
          type="product"
          jsonLd={jsonLd}
        />
      )}

      <div className="container-site py-6 lg:py-10">
        <button
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          data-testid="pdp-back-button"
        >
          <ChevronLeft className="h-4 w-4" /> {t("common.back")}
        </button>

        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><LangLink to="/">Home</LangLink></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><LangLink to="/brand/kentucky">Shop</LangLink></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage className="line-clamp-1">{product?.title || handle}</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* GALLERY */}
          <div className="min-w-0" data-testid="pdp-gallery">
            {loading ? (
              <Skeleton className="aspect-square w-full rounded-2xl" />
            ) : (
              <>
                <motion.div
                  key={activeImage}
                  initial={{ opacity: 0.6 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-white"
                >
                  {images[activeImage] ? (
                    <img
                      src={proxyImage(images[activeImage], 1000)}
                      alt={product.title}
                      decoding="async"
                      fetchPriority="high"
                      className="h-full w-full object-contain p-6"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No image</div>
                  )}
                </motion.div>
                {images.length > 1 && (
                  <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar">
                    {images.slice(0, 8).map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        data-testid="pdp-thumbnail"
                        className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border bg-white transition-all ${
                          i === activeImage ? "border-boomerang ring-2 ring-boomerang/30" : "border-border hover:border-foreground/30"
                        }`}
                      >
                        <img src={proxyImage(img, 200)} alt={`${product.title} view ${i + 1}`} loading="lazy" decoding="async" className="h-full w-full object-contain p-1.5" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* DETAILS */}
          <div className="min-w-0">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : (
              <>
                {product.vendor && (
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-boomerang" data-testid="pdp-vendor">{product.vendor}</p>
                )}
                <h1 className="mt-2 font-display text-3xl leading-tight break-words sm:text-4xl" data-testid="pdp-title">{product.title}</h1>

                <div className="mt-4 flex items-center gap-3">
                  <span className="font-display text-2xl" data-testid="pdp-price">{price && formatPrice(price)}</span>
                  {currentVariant?.available ? (
                    <Badge variant="secondary" className="gap-1 text-xs"><Check className="h-3 w-3" /> {t("common.in_stock")}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground">{t("common.out_of_stock")}</Badge>
                  )}
                  {lowStock && (
                    <span className="text-xs font-medium text-destructive" data-testid="pdp-low-stock">
                      {tx("Only")} {currentVariant.quantity_available} {tx("left")}
                    </span>
                  )}
                </div>

                {/* OPTIONS */}
                {optionGroups.length > 0 && optionGroups[0].values[0] !== "Default Title" && (
                  <div className="mt-7 space-y-5" data-testid="pdp-options">
                    {optionGroups.map((group) => (
                      <div key={group.name}>
                        <p className="text-sm font-medium">
                          {group.name}: <span className="text-muted-foreground">{selected[group.name]}</span>
                        </p>
                        <div className="mt-2.5 flex flex-wrap gap-2">
                          {group.values.map((val) => {
                            const isSel = selected[group.name] === val;
                            const avail = isValueAvailable(group.name, val);
                            return (
                              <button
                                key={val}
                                onClick={() => setSelected((s) => ({ ...s, [group.name]: val }))}
                                data-testid="pdp-option-value"
                                className={`rounded-md border px-3.5 py-2 text-sm transition-all ${
                                  isSel
                                    ? "border-boomerang bg-boomerang/10 font-medium text-foreground"
                                    : avail
                                    ? "border-border hover:border-foreground/40"
                                    : "border-border/60 text-muted-foreground line-through opacity-50"
                                }`}
                              >
                                {val}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* QTY + ADD */}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <div className="flex items-center rounded-md border border-border">
                    <button
                      aria-label="Decrease quantity"
                      data-testid="pdp-qty-decrease"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="px-3 py-3 text-muted-foreground hover:text-foreground"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-[40px] text-center text-sm font-medium" data-testid="pdp-qty-value">{qty}</span>
                    <button
                      aria-label="Increase quantity"
                      data-testid="pdp-qty-increase"
                      onClick={() => setQty((q) => Math.min(maxStock, q + 1))}
                      disabled={qty >= maxStock}
                      className="px-3 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {currentVariant?.available ? (
                    <Button
                      size="lg"
                      className="flex-1 min-w-[200px]"
                      onClick={handleAdd}
                      data-testid="pdp-add-to-cart-button"
                    >
                      {justAdded ? (
                        <><Check className="mr-2 h-5 w-5" /> {t("common.added")}</>
                      ) : (
                        <><ShoppingBag className="mr-2 h-5 w-5" /> {t("common.add_to_cart")}</>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="flex-1 min-w-[200px]"
                      onClick={() => setBackorderOpen(true)}
                      data-testid="pdp-request-backorder-button"
                    >
                      <PackageSearch className="mr-2 h-5 w-5" /> {tx("Request Backorder")}
                    </Button>
                  )}
                </div>
                {!currentVariant?.available && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="pdp-backorder-hint">
                    {tx("Out of stock — request a backorder and we'll notify you when it returns.")}
                  </p>
                )}

                {/* TRUST */}
                <div className="mt-7 grid grid-cols-1 gap-3 rounded-xl border border-border bg-secondary/40 p-4 sm:grid-cols-3">
                  {[
                    { icon: Truck, t: t("trust.shipping") },
                    { icon: ShieldCheck, t: t("trust.authentic") },
                    { icon: RotateCcw, t: t("trust.returns") },
                  ].map((f) => (
                    <div key={f.t} className="flex items-center gap-2 text-xs font-medium">
                      <f.icon className="h-4 w-4 shrink-0 text-boomerang" /> {f.t}
                    </div>
                  ))}
                </div>

                {/* DESCRIPTION */}
                {(product.description_html || product.description) && (
                  <div className="mt-8" data-testid="pdp-description">
                    <h2 className="font-display text-xl">{t("common.product_details")}</h2>
                    {product.description_html ? (
                      <div
                        className="prose prose-sm prose-neutral mt-3 max-w-none break-words [&_*]:max-w-full [&_img]:h-auto [&_img]:max-w-full [&_iframe]:max-w-full [&_video]:max-w-full [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: product.description_html }}
                      />
                    ) : (
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{product.description}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <BackorderDialog
        open={backorderOpen}
        onOpenChange={setBackorderOpen}
        product={product}
        variant={currentVariant}
      />
    </>
  );
}
